/**
 * What the live board shows, and how a cleared bid changes it.
 *
 * This module is deliberately free of React and of the DOM: it is the board's
 * reasoning, and the component above it is only the motion. A row's position
 * is never asserted by an animation — it falls out of the same ordering the
 * server uses, so the reshuffle a watcher sees is the reshuffle that happened.
 */

import type { BidEvent, RankedEntry, TickerEvent } from '@bidtohire/shared';

/** How many rows the hero board holds. Four, per the approved concept. */
export const BOARD_SIZE = 4;

/** One row of the hero board, flattened to exactly what a row renders. */
export interface BoardRow {
  readonly jobId: string;
  readonly title: string;
  readonly companyName: string;
  readonly companySlug: string;
  readonly companyLogoKey?: string;
  readonly salaryLabel?: string;
  readonly totalCents: number;
  /**
   * When this listing's first payment landed, which is how the server breaks a
   * tie: at equal totals the older bid keeps the higher position.
   */
  readonly since: string;
}

/**
 * The board's ordering: lifetime total, highest first; ties to the earlier
 * first payment. This mirrors the rank sort key exactly, so an optimistic
 * reorder from a pushed event lands where the next read would have put it.
 */
export function compareRows(a: BoardRow, b: BoardRow): number {
  if (b.totalCents !== a.totalCents) return b.totalCents - a.totalCents;
  const left = Date.parse(a.since);
  const right = Date.parse(b.since);
  if (Number.isNaN(left) || Number.isNaN(right) || left === right) {
    return a.jobId.localeCompare(b.jobId);
  }
  return left - right;
}

export function rowFromEntry(entry: RankedEntry): BoardRow {
  return {
    jobId: entry.jobId,
    title: entry.title,
    companyName: entry.company.name,
    companySlug: entry.company.slug,
    ...(entry.company.logoKey === undefined ? {} : { companyLogoKey: entry.company.logoKey }),
    ...(entry.compensationLabel === undefined ? {} : { salaryLabel: entry.compensationLabel }),
    totalCents: entry.totalPaidCents,
    since: entry.firstPaidAt ?? entry.createdAt,
  };
}

/** The top rows of a board, as fetched. */
export function rowsFromEntries(
  entries: readonly RankedEntry[],
  size = BOARD_SIZE,
): BoardRow[] {
  return entries.slice(0, size).map(rowFromEntry);
}

export function rowFromBid(event: BidEvent): BoardRow {
  return {
    jobId: event.jobId,
    title: event.title,
    companyName: event.companyName,
    companySlug: event.companySlug,
    ...(event.companyLogoKey === undefined ? {} : { companyLogoKey: event.companyLogoKey }),
    ...(event.compensationLabel === undefined ? {} : { salaryLabel: event.compensationLabel }),
    totalCents: event.totalCents,
    since: event.at,
  };
}

/** What a bid did to the board, which is what decides the motion played. */
export type BidOutcome =
  /** A row already on the board moved, or held its place at a new figure. */
  | 'raised'
  /** A listing arrived from off the board and displaced the last row. */
  | 'entered'
  /** The bid was real but happened below the four rows shown. */
  | 'unseen';

export interface BidApplication {
  /** The board as it now stands, in its new order. */
  readonly rows: readonly BoardRow[];
  /**
   * The half-step between: the new figure, in the old order.
   *
   * The concept rolls the money before the row travels, so the interface needs
   * a state where the amount has changed and nothing has moved yet. It is the
   * same data either way — only the order differs.
   */
  readonly staged: readonly BoardRow[];
  readonly outcome: BidOutcome;
  /** The row that moved, when one did. */
  readonly movedJobId?: string;
}

/**
 * Apply a cleared bid to the visible board.
 *
 * A bid for a listing already shown updates that row's figure and re-sorts. A
 * bid for a listing that is not shown enters the board only if its new total
 * genuinely belongs there — the event carries every field a row renders, so the
 * entrance is drawn from the payload rather than guessed at while a refetch is
 * in flight. A bid that changes nothing visible returns the same array, which
 * is what stops the board twitching for activity a watcher cannot see.
 */
export function applyBid(
  rows: readonly BoardRow[],
  event: BidEvent,
  size = BOARD_SIZE,
): BidApplication {
  const incoming = rowFromBid(event);
  const index = rows.findIndex((row) => row.jobId === event.jobId);

  if (index >= 0) {
    const existing = rows[index] as BoardRow;
    if (existing.totalCents === event.totalCents) {
      return { rows, staged: rows, outcome: 'unseen' };
    }
    // The stored `since` wins: it is the listing's real first payment, while
    // the event's timestamp is this payment's. They differ on every raise.
    const staged = rows.map((row) =>
      row.jobId === event.jobId ? { ...row, ...incoming, since: row.since } : row,
    );
    return {
      rows: [...staged].sort(compareRows),
      staged,
      outcome: 'raised',
      movedJobId: event.jobId,
    };
  }

  const contenders = [...rows, incoming].sort(compareRows);
  const kept = contenders.slice(0, size);
  if (!kept.some((row) => row.jobId === event.jobId)) {
    return { rows, staged: rows, outcome: 'unseen' };
  }

  // Nothing to stage: the row is not on the board yet, so there is no figure
  // sitting in front of anyone to roll.
  return { rows: kept, staged: rows, outcome: 'entered', movedJobId: event.jobId };
}

/**
 * Fold a freshly read board into the rows on screen.
 *
 * The read is the truth, so it replaces what is shown — but a row that was
 * already there keeps its identity, which is what lets the component animate
 * from where the row is to where it now belongs instead of redrawing the board
 * from nothing.
 */
export function reconcile(
  rows: readonly BoardRow[],
  entries: readonly RankedEntry[],
  size = BOARD_SIZE,
): { readonly rows: readonly BoardRow[]; readonly changed: boolean } {
  const next = rowsFromEntries(entries, size);
  const changed =
    next.length !== rows.length ||
    next.some((row, position) => {
      const current = rows[position];
      return (
        current === undefined ||
        current.jobId !== row.jobId ||
        current.totalCents !== row.totalCents
      );
    });
  return { rows: changed ? next : rows, changed };
}

/** The same bid, as the activity feed states it. */
export function tickerEventFromBid(event: BidEvent): TickerEvent {
  return {
    jobId: event.jobId,
    jobTitle: event.title,
    jobSlug: event.jobSlug,
    companyName: event.companyName,
    companySlug: event.companySlug,
    // The live channel is the one place the size of a single raise is known:
    // a board row only ever states a lifetime total.
    amountCents: event.deltaCents,
    totalCents: event.totalCents,
    at: event.at,
  };
}

/** Newest first, one entry per listing, capped at what the feed shows. */
export function mergeTickerEvents(
  current: readonly TickerEvent[],
  incoming: readonly TickerEvent[],
  limit = 6,
): TickerEvent[] {
  const merged = [...incoming, ...current].sort((a, b) => Date.parse(b.at) - Date.parse(a.at));
  const seen = new Set<string>();
  const unique: TickerEvent[] = [];
  for (const event of merged) {
    if (seen.has(event.jobId)) continue;
    seen.add(event.jobId);
    unique.push(event);
    if (unique.length >= limit) break;
  }
  return unique;
}
